import { useState, useCallback, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Pencil,
  X as XIcon,
  Check,
} from 'lucide-react';
import type { SprintListItem, SprintItem } from '../../services/sprintService';
import {
  useUpdateSprint,
  useDeleteSprint,
  useStartSprint,
  useCompleteSprint,
  useRemoveItemFromSprint,
} from '../../hooks/useSprints';
import { PRIORITY_CONFIG } from '../board/boardUtils';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';
import { usePanelContext } from '../../contexts/PanelContext';
import SprintCompleteModal from './SprintCompleteModal';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';

interface Props {
  sprint: SprintListItem;
  projectId: string;
  allSprints: SprintListItem[];
  onDropIssue: (sprintId: string, issueId: string) => void;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  PLANNED: { label: '계획됨', cls: 'bg-gray-100 text-gray-600' },
  ACTIVE: { label: '활성', cls: 'bg-green-100 text-green-700' },
  COMPLETED: { label: '완료됨', cls: 'bg-blue-100 text-blue-600' },
};

export default function SprintColumn({ sprint, projectId, allSprints, onDropIssue }: Props) {
  const { openDetailPanel } = usePanelContext();
  const [collapsed, setCollapsed] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(sprint.name);
  const [showCompleteModal, setShowCompleteModal] = useState(false);

  const updateMutation = useUpdateSprint(projectId);
  const deleteMutation = useDeleteSprint(projectId);
  const startMutation = useStartSprint(projectId);
  const completeMutation = useCompleteSprint(projectId);
  const removeItemMutation = useRemoveItemFromSprint(projectId);

  const badge = STATUS_BADGE[sprint.status] ?? STATUS_BADGE.PLANNED;

  const totalPoints = useMemo(
    () =>
      sprint.items.reduce(
        (sum, item) => sum + (item.issue.storyPoints ?? 0),
        0,
      ),
    [sprint.items],
  );

  // --- Drag & Drop handlers ---
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      try {
        const payload = JSON.parse(e.dataTransfer.getData('application/json'));
        if (payload.issueId) {
          onDropIssue(sprint.sprintId, payload.issueId);
        }
      } catch {
        // ignore invalid drop
      }
    },
    [sprint.sprintId, onDropIssue],
  );

  // --- Inline name editing ---
  const handleSaveName = () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== sprint.name) {
      updateMutation.mutate({ sprintId: sprint.sprintId, data: { name: trimmed } });
    }
    setEditingName(false);
  };

  const handleCancelEdit = () => {
    setNameValue(sprint.name);
    setEditingName(false);
  };

  // --- Actions ---
  const handleStart = () => startMutation.mutate(sprint.sprintId);
  const handleCompleteConfirm = (carryOverToSprintId?: string) => {
    completeMutation.mutate(
      { sprintId: sprint.sprintId, data: carryOverToSprintId ? { carryOverToSprintId } : undefined },
      { onSuccess: () => setShowCompleteModal(false) },
    );
  };
  const handleDelete = () => {
    if (window.confirm(`"${sprint.name}" 스프린트를 삭제하시겠습니까?`)) {
      deleteMutation.mutate(sprint.sprintId);
    }
  };

  const handleRemoveItem = (issueId: string) => {
    removeItemMutation.mutate({ sprintId: sprint.sprintId, issueId });
  };

  const dateRange = useMemo(() => {
    if (!sprint.startDate && !sprint.endDate) return null;
    const fmt = (d: string) => {
      const dt = new Date(d);
      return `${dt.getMonth() + 1}/${dt.getDate()}`;
    };
    if (sprint.startDate && sprint.endDate)
      return `${fmt(sprint.startDate)} – ${fmt(sprint.endDate)}`;
    if (sprint.startDate) return `${fmt(sprint.startDate)} –`;
    return `– ${fmt(sprint.endDate!)}`;
  }, [sprint.startDate, sprint.endDate]);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`w-[300px] shrink-0 rounded-lg border transition-colors self-start ${
        dragOver
          ? 'border-blue-400 bg-blue-50/50'
          : 'border-gray-200 bg-white'
      }`}
    >
      {/* Sprint Header */}
      <div className="px-3 py-2 border-b border-gray-100 space-y-1">
        {/* 1행: 제목 */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {/* Name (inline edit) */}
          {editingName ? (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <input
                autoFocus
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveName();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
                className="text-sm font-semibold text-gray-800 border border-blue-300 rounded px-1.5 py-0.5 flex-1 min-w-0 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button onClick={handleSaveName} className="text-green-600 hover:text-green-700">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={handleCancelEdit} className="text-gray-400 hover:text-gray-600">
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setNameValue(sprint.name);
                setEditingName(true);
              }}
              className="flex items-center gap-1 text-sm font-semibold text-gray-800 hover:text-blue-600 min-w-0"
            >
              <span className="truncate">{sprint.name}</span>
              <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 shrink-0" />
            </button>
          )}

          <span className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${badge.cls}`}>
            {badge.label}
          </span>
        </div>

        {/* 2행: 메타 정보 + 액션 버튼 */}
        <div className="flex items-center gap-2 pl-6">
          <span className="text-[11px] text-gray-400">
            {sprint.items.length}건 · {totalPoints}pt
          </span>
          {dateRange && (
            <span className="text-[11px] text-gray-400">{dateRange}</span>
          )}

          {/* Action buttons */}
          <div className="ml-auto flex items-center gap-1 shrink-0">
          {sprint.status === 'PLANNED' && (
            <>
              <button
                onClick={handleStart}
                disabled={startMutation.isPending}
                className="text-[11px] px-2 py-0.5 rounded border border-green-300 text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50"
              >
                시작
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="text-[11px] px-2 py-0.5 rounded border border-red-300 text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                삭제
              </button>
            </>
          )}
          {sprint.status === 'ACTIVE' && (
            <button
              onClick={() => setShowCompleteModal(true)}
              className="text-[11px] px-2 py-0.5 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors"
            >
              종료
            </button>
          )}
        </div>
      </div>
      </div>

      {/* Items */}
      {!collapsed && (
        <div className="divide-y divide-gray-50">
          {sprint.items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-gray-400">
              작업을 드래그하여 추가하세요
            </div>
          ) : (
            sprint.items.map((item) => (
              <SprintItemRow
                key={item.sprintItemId}
                item={item}
                onRemove={() => handleRemoveItem(item.issue.id)}
                onClick={() => openDetailPanel('issue', item.issue.id)}
              />
            ))
          )}
        </div>
      )}

      {/* Complete Modal */}
      <SprintCompleteModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        sprint={sprint}
        allSprints={allSprints}
        onComplete={handleCompleteConfirm}
        isSubmitting={completeMutation.isPending}
      />
    </div>
  );
}

// --- SprintItemRow ---

function SprintItemRow({
  item,
  onRemove,
  onClick,
}: {
  item: SprintItem;
  onRemove: () => void;
  onClick: () => void;
}) {
  const { openProfileModal } = useProfileModal();
  const typeConfig = resolveIssueTypeConfig(item.issue.issueTypeDef, item.issue.issueType);
  const priorityConfig = PRIORITY_CONFIG[item.issue.priority];

  return (
    <div className="group flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 transition-colors">
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-gray-400 font-mono shrink-0">
            {item.issue.issueId}
          </span>
          {typeConfig && (
            <typeConfig.Icon size={12} className={`shrink-0 ${typeConfig.color}`} />
          )}
          <span className="text-sm text-gray-800 truncate">{item.issue.title}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500">
          {priorityConfig && (
            <span className={priorityConfig.color}>{priorityConfig.label}</span>
          )}
          {item.issue.storyPoints != null && (
            <span className="bg-gray-100 text-gray-600 px-1 rounded">
              {item.issue.storyPoints}pt
            </span>
          )}
          {item.issue.assignee && (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Avatar
                size="xs"
                userId={item.issue.assignee.id}
                name={item.issue.assignee.name}
                avatarUrl={item.issue.assignee.avatarUrl}
                displayColor={item.issue.assignee.displayColor as ProfileColorKey | null | undefined}
                firstName={item.issue.assignee.firstName}
                onClick={() => openProfileModal(item.issue.assignee!.id)}
              />
              <span className="truncate">{item.issue.assignee.name}</span>
            </div>
          )}
        </div>
      </div>
      <button
        onClick={onRemove}
        className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-500 transition-all"
        title="스프린트에서 제거"
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
